import { FC, useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import styles from '../../styles/index.module.scss';
import defaultStyles from './styles/CategoricalGroup.module.scss'
import { PluginWrapper } from "../../../components/plugins";
import { t } from "i18next";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { CategoricalGroupUnionPluginSettings } from "./pluginSettings/CategoricalGroupUnionPluginSettings";
import { MainCategoricalGroup2GoodsUnionPluginSettings } from "./pluginSettings/MainCategoricalGroup2GoodsUnionPluginSettings";
import { CategoricalGroupLinkUnionPluginSettings } from "./pluginSettings/CategoricalGroupLinkUnionPluginSettings";
import { RelatedCategoricalGroup2GoodsUnionPluginSettings } from "./pluginSettings/RelatedCategoricalGroup2GoodsUnionPluginSettings";
import { CategoricalGroup2GoodsUnionDataProvider, CategoricalGroupLinkUnionDataProvider, CategoricalGroupUnionDataProvider } from "../../../Services/DataProviders/CategoricalGroupDataProvider";
import { ICategoricalGroup2GoodsUnionViewDTO, ICategoricalGroupLinkUnionViewDTO, ICategoricalGroupUnionViewDTO } from "../../../libs/coreapi-dto/dirs/categoricalGroup";
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { Spinner } from "../../../components/spiner/Spinner";
import InputSearch from "../../../components/inputSearch/inputSearch";
import BaseCommandsPanel from "../../../components/commandsPanels/BaseCommandsPanel";
import Tooltip from "../../../components/tooltips/Tooltip";
import { RotationRightButton } from "../../../components/buttons/iconButtons/action/RotationCollection";

export const CategoricalGroupView: FC = () => {
    const appCtx = useAppContext();
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [resetTrigger, setResetTrigger] = useState(0);

    const categoricalGroupUnionFilter: IGridFilter = {
    numberPerPage: 15,
    pageNumber: 1,
        columnFilters: [
            {
                name: "hasLinks",
                operator: 'Eq',
                value: true
            },
            {
                name: "deleted",
                operator: "Eq",
                value: false,
            },
        ]
};

    //1 грид
    const categoricalGroupUnionDataProvider = new CategoricalGroupUnionDataProvider(appCtx.coreApiService);
    const [categoricalGroupUnionData, setCategoricalGroupUnionData] = useState<ICategoricalGroupUnionViewDTO[]>([]);
    const [categoricalGroupUnionTotalCount, setCategoricalGroupUnionTotalCount] = useState<number>(0);
    const [categoricalGroupUnionGridFilter, dispatchCategoricalGroupUnionGridFilter] = useGridFilter(categoricalGroupUnionFilter);
    const [categoricalGroupUnionSelectedItem, setCategoricalGroupUnionSelectedItem] = useState<IGridRow>();
    const [isCategoricalGroupUnionDataLoading, setIsCategoricalGroupUnionDataLoading] = useState<boolean>(false);

    //2 грид
    const categoricalGroup2GoodsUnionDataProvider = new CategoricalGroup2GoodsUnionDataProvider(appCtx.coreApiService);
    const [categoricalGroup2GoodsUnionData, setCategoricalGroup2GoodsUnionData] = useState<ICategoricalGroup2GoodsUnionViewDTO[]>([]);
    const [categoricalGroup2GoodsUnionTotalCount, setCategoricalGroup2GoodsUnionTotalCount] = useState<number>(0);
    const [categoricalGroup2GoodsUnionGridFilter, dispatchCategoricalGroup2GoodsUnionGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [categoricalGroup2GoodsUnionSelectedItem, setCategoricalGroup2GoodsUnionSelectedItem] = useState<IGridRow>();
    const [isCategoricalGroup2GoodsUnionDataLoading, setIsCategoricalGroup2GoodsUnionDataLoading] = useState<boolean>(false);

    //3 грид
    const categoricalGroupLinkUnionDataProvider = new CategoricalGroupLinkUnionDataProvider(appCtx.coreApiService);
    const [categoricalGroupLinkUnionData, setCategoricalGroupLinkUnionData] = useState<ICategoricalGroupLinkUnionViewDTO[]>([]);
    const [categoricalGroupLinkUnionTotalCount, setCategoricalGroupLinkUnionTotalCount] = useState<number>(0);
    const [categoricalGroupLinkUnionGridFilter, dispatchCategoricalGroupLinkUnionGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [categoricalGroupLinkUnionSelectedItem, setCategoricalGroupLinkUnionSelectedItem] = useState<IGridRow>();
    const [isCategoricalGroupLinkUnionDataLoading, setIsCategoricalGroupLinkUnionDataLoading] = useState<boolean>(false);

    //4 грид
    const [relatedCategoricalGroup2GoodsUnionData, setRelatedCategoricalGroup2GoodsUnionData] = useState<ICategoricalGroup2GoodsUnionViewDTO[]>([]);
    const [relatedCategoricalGroup2GoodsUnionTotalCount, setRelatedCategoricalGroup2GoodsUnionTotalCount] = useState<number>(0);
    const [relatedCategoricalGroup2GoodsUnionGridFilter, dispatchRelatedCategoricalGroup2GoodsUnionGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [relatedCategoricalGroup2GoodsUnionSelectedItem, setRelatedCategoricalGroup2GoodsUnionSelectedItem] = useState<IGridRow>();
    const [isRelatedCategoricalGroup2GoodsUnionDataLoading, setIsRelatedCategoricalGroup2GoodsUnionDataLoading] = useState<boolean>(false);

    const baseT = (value: string) => t('directory.categoricalGroup.' + value);

    const abortControllersRef = useRef<{
        grid1?: AbortController;
        grid2?: AbortController;
        grid3?: AbortController;
        grid4?: AbortController;
    }>({});

     // Функция для безопасного выполнения запроса
    const executeRequest = useCallback((
        gridKey: 'grid1' | 'grid2' | 'grid3' | 'grid4',
        requestFn: (signal: AbortSignal) => void,
        onComplete?: () => void
    ) => {
        // Отменяем предыдущий запрос для этого грида
        if (abortControllersRef.current[gridKey]) {
            abortControllersRef.current[gridKey]?.abort('cancel');
        }

        const controller = new AbortController();
        abortControllersRef.current[gridKey] = controller;

        try {
            requestFn(controller.signal);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                console.log('Request aborted');
            } else {
                console.error('Request error:', error);
            }
        } finally {
            delete abortControllersRef.current[gridKey];
            onComplete?.();
        }

        return () => controller.abort('abort');
    }, []);

    //1 Грид
    function getCategoricalGroupUnion() {
        setIsCategoricalGroupUnionDataLoading(true);

        return executeRequest('grid1', (signal) => {

            categoricalGroupUnionDataProvider.viewParam(
                categoricalGroupUnionGridFilter,
                (data, totalCount) => {
                    if (signal.aborted) return;
                    
                    // Очищаем 2, 3, 4 гриды
                    setCategoricalGroup2GoodsUnionData([]);
                    setCategoricalGroupLinkUnionData([]);
                    setRelatedCategoricalGroup2GoodsUnionData([]);
                    setCategoricalGroup2GoodsUnionTotalCount(0);
                    setCategoricalGroupLinkUnionTotalCount(0);
                    setRelatedCategoricalGroup2GoodsUnionTotalCount(0);

                    setCategoricalGroupUnionSelectedItem(undefined);

                    setCategoricalGroupUnionData(data);
                    setCategoricalGroupUnionTotalCount(totalCount);

                    setIsCategoricalGroupUnionDataLoading(false);                    
                    data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
                },
                signal
            );
        });
    }

    useEffect(() => {
        const cleanup = getCategoricalGroupUnion();
        return cleanup;
    }, [categoricalGroupUnionGridFilter]);

    //2 Грид логика - Для фильтрации по группе необходимо задать фильтр "idCategoricalGroupGlobal".
    const getCategoricalGroup2GoodsUnionFilter = useCallback((): IGridFilter => {
        if (!categoricalGroupUnionSelectedItem) {
            return categoricalGroup2GoodsUnionGridFilter
        }

        const categoricalGroup2GoodsUnionFilter = {
            ...categoricalGroup2GoodsUnionGridFilter,
            columnFilters: [
                ...categoricalGroup2GoodsUnionGridFilter.columnFilters,
                {
                    name: "idCategoricalGroupGlobal",
                    operator: 'Eq',
                    value: categoricalGroupUnionSelectedItem.idGlobal
                }
            ]
        } as IGridFilter
        return categoricalGroup2GoodsUnionFilter
    }, [categoricalGroupUnionSelectedItem, categoricalGroup2GoodsUnionGridFilter])

    function getCategoricalGroup2GoodsUnion() {
        setIsCategoricalGroup2GoodsUnionDataLoading(true);

        return executeRequest('grid2', (signal) => {
            categoricalGroup2GoodsUnionDataProvider.viewParam(
                getCategoricalGroup2GoodsUnionFilter(),
                (data, totalCount) => {
                    if (signal.aborted) return;
                    
                    setCategoricalGroup2GoodsUnionData(data);
                    setCategoricalGroup2GoodsUnionTotalCount(totalCount);
                    setIsCategoricalGroup2GoodsUnionDataLoading(false);
                },
                signal
            );
        });
    }

    useEffect(() => {
        if (!categoricalGroupUnionSelectedItem) return;
        
        const cleanup = getCategoricalGroup2GoodsUnion();
        return cleanup;
    }, [categoricalGroupUnionSelectedItem, categoricalGroup2GoodsUnionGridFilter]);

    //3 Грид логика - Для фильтрации по группе необходимо задать фильтр "idLinkedCategoricalGroupGlobal"
    const getCategoricalGroupLinkUnionFilter = useCallback((): IGridFilter => {
        if (!categoricalGroupUnionSelectedItem) {
            return categoricalGroupLinkUnionGridFilter
        }

        const categoricalGroupLinkUnionFilter = {
            ...categoricalGroupLinkUnionGridFilter,
            columnFilters: [
                ...categoricalGroupLinkUnionGridFilter.columnFilters,
                {
                    name: "idLinkedCategoricalGroupGlobal",
                    operator: 'Eq',
                    value: categoricalGroupUnionSelectedItem.idGlobal
                }
            ]
        } as IGridFilter
        return categoricalGroupLinkUnionFilter
    }, [categoricalGroupUnionSelectedItem, categoricalGroupLinkUnionGridFilter])

    function getCategoricalGroupLinkUnion() {
        setIsCategoricalGroupLinkUnionDataLoading(true);

        return executeRequest('grid3', (signal) => {
            categoricalGroupLinkUnionDataProvider.viewParam(
                getCategoricalGroupLinkUnionFilter(),
                (data, totalCount) => {
                    if (signal.aborted) return;
                    
                    // Очищаем 4 грид
                    setRelatedCategoricalGroup2GoodsUnionData([]);
                    setRelatedCategoricalGroup2GoodsUnionTotalCount(0);
                    setRelatedCategoricalGroup2GoodsUnionSelectedItem(undefined);

                    setCategoricalGroupLinkUnionData(data);
                    setCategoricalGroupLinkUnionTotalCount(totalCount);

                    setIsCategoricalGroupLinkUnionDataLoading(false);
                },
                signal
            );
        });
    }

    useEffect(() => {
        if (!categoricalGroupUnionSelectedItem) return;

        const cleanup = getCategoricalGroupLinkUnion();
        return cleanup;
    }, [categoricalGroupUnionSelectedItem, categoricalGroupLinkUnionGridFilter]);

    //4 Грид - логика делать запрос по товарам с фильтром по полю "idCategoricalGroupGlobal"
    const getRelatedCategoricalGroup2GoodsUnionFilter = useCallback((): IGridFilter => {
        if (!categoricalGroupLinkUnionSelectedItem) {
            return relatedCategoricalGroup2GoodsUnionGridFilter
        }

        const relatedCategoricalGroup2GoodsUnionFilter = {
            ...relatedCategoricalGroup2GoodsUnionGridFilter,
            columnFilters: [
                ...relatedCategoricalGroup2GoodsUnionGridFilter.columnFilters,
                {
                    name: "idCategoricalGroupGlobal",
                    operator: 'Eq',
                    value: categoricalGroupLinkUnionSelectedItem.idCategoricalGroupGlobal
                }
            ]
        } as IGridFilter
        return relatedCategoricalGroup2GoodsUnionFilter
    }, [categoricalGroupLinkUnionSelectedItem, relatedCategoricalGroup2GoodsUnionGridFilter])
   
    function getRelatedCategoricalGroup2GoodsUnion() {
        setIsRelatedCategoricalGroup2GoodsUnionDataLoading(true);

        return executeRequest('grid4', (signal) => {
            categoricalGroup2GoodsUnionDataProvider.viewParam(
                getRelatedCategoricalGroup2GoodsUnionFilter(),
                (data, totalCount) => {
                    if (signal.aborted) return;
                    
                    setRelatedCategoricalGroup2GoodsUnionData(data);
                    setRelatedCategoricalGroup2GoodsUnionTotalCount(totalCount);
                    setIsRelatedCategoricalGroup2GoodsUnionDataLoading(false);
                },
                signal
            );
        });
    }

    useEffect(() => {
        if (!categoricalGroupLinkUnionSelectedItem) return;

        const cleanup = getRelatedCategoricalGroup2GoodsUnion();
        return cleanup;
    }, [categoricalGroupLinkUnionSelectedItem, relatedCategoricalGroup2GoodsUnionGridFilter]);

    const handleRefresh = () => {
        setCategoricalGroupUnionSelectedItem(undefined);
        setCategoricalGroup2GoodsUnionSelectedItem(undefined);
        setCategoricalGroupLinkUnionSelectedItem(undefined);
        setRelatedCategoricalGroup2GoodsUnionSelectedItem(undefined);

        setResetTrigger(prev => prev + 1);
    }

    return (
        <PluginWrapper>
            <div className={defaultStyles.rowGrids}>
                {/* 1 Грид */}
                <div className={defaultStyles.blockWrapper}>
                    <div className={defaultStyles.searchBlock} style={{marginTop: -5}}>
                        <BaseCommandsPanel groups={[
                            [
                                {
                                    iconButton: <Tooltip title={baseT('refresh')}>
                                        <RotationRightButton
                                            sizeVariant="mini"
                                            onClick={handleRefresh}
                                            disabled={!LoadingStatus.Completed}
                                        />
                                    </Tooltip>
                                },

                            ],
                        ]}
                        />
                        <InputSearch
                            style={{marginTop: 5}}
                            searchValue={''}
                            typeFilter={'columnFilter'}
                            operator="Like"
                            gridFilter={categoricalGroupUnionGridFilter}
                            dispatchGridFilter={dispatchCategoricalGroupUnionGridFilter}
                            resetTrigger={resetTrigger}
                            filterName="name"
                            placeholder={baseT("searchByName")}
                            debounceDelay={1000}
                        />
                    </div>
                    {
                        isCategoricalGroupUnionDataLoading
                            ? <div><Spinner /></div>
                            : <div className={styles.gridWrapper}>
                                <div className={styles.masterGrid}>
                                    <DefaultGrid
                                        // hideSettingsAndExportButton
                                        showQuantity
                                        searching
                                        isServerFilter
                                        gridId={uuidv4()}
                                        data={categoricalGroupUnionData}
                                        loadingStatus={loadingStatus}
                                        setLoadingStatus={setLoadingStatus}
                                        dataProvider={categoricalGroupUnionDataProvider}
                                        plugin={CategoricalGroupUnionPluginSettings()}
                                        totalCount={categoricalGroupUnionTotalCount}
                                        filter={categoricalGroupUnionGridFilter}
                                        selectedItem={categoricalGroupUnionSelectedItem}
                                        getView={(categoricalGroupUnionGridFilter, callback) => {
                                            categoricalGroupUnionDataProvider.viewParam(categoricalGroupUnionGridFilter, (data, totalCount) => {
                                                callback(data, totalCount)
                                            })
                                        }}
                                        onSelect={(row) => {
                                            setCategoricalGroupUnionSelectedItem(row)
                                        }}
                                        onSort={(i) => dispatchCategoricalGroupUnionGridFilter({ type: "sort", payload: i.propertyName })}
                                        onFilterDelete={(i) => dispatchCategoricalGroupUnionGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                        onPageNumberChange={(n) => dispatchCategoricalGroupUnionGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                        onNumberPerPageChange={(n) => dispatchCategoricalGroupUnionGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                    />
                                </div>
                            </div>
                    }
                </div>
                {/* 2 Грид */}
                <div className={defaultStyles.blockWrapper}>
                    <div className={defaultStyles.searchBlock}>
                        <div className={defaultStyles.codeSearchBlock}>
                            <InputSearch
                                searchValue={''}
                                typeFilter={'columnFilter'}
                                gridFilter={categoricalGroup2GoodsUnionGridFilter}
                                dispatchGridFilter={dispatchCategoricalGroup2GoodsUnionGridFilter}
                                resetTrigger={resetTrigger}
                                filterName="goodsCode"
                                placeholder={baseT("searchByCode")}
                                debounceDelay={1000}
                            />
                        </div>
                        <div className={defaultStyles.goodsSearchBlock}>
                            <InputSearch
                                searchValue={''}
                                typeFilter={'columnFilter'}
                                operator="Like"
                                gridFilter={categoricalGroup2GoodsUnionGridFilter}
                                dispatchGridFilter={dispatchCategoricalGroup2GoodsUnionGridFilter}
                                resetTrigger={resetTrigger}
                                filterName="goodsName"
                                placeholder={baseT("searchByName")}
                                debounceDelay={1000}
                            />
                        </div>
                    </div>
                    {
                        isCategoricalGroup2GoodsUnionDataLoading
                            ? <div><Spinner /></div>
                            : <div className={styles.gridWrapper}>
                                <div className={styles.masterGrid}>
                                    <DefaultGrid
                                        showQuantity
                                        // hideSettingsAndExportButton
                                        gridId={uuidv4()}
                                        data={categoricalGroup2GoodsUnionData}
                                        loadingStatus={loadingStatus}
                                        setLoadingStatus={setLoadingStatus}
                                        dataProvider={categoricalGroup2GoodsUnionDataProvider}
                                        plugin={MainCategoricalGroup2GoodsUnionPluginSettings()}
                                        totalCount={categoricalGroup2GoodsUnionTotalCount}
                                        filter={categoricalGroup2GoodsUnionGridFilter}
                                        selectedItem={categoricalGroup2GoodsUnionSelectedItem}
                                        getView={(categoricalGroup2GoodsUnionGridFilter, callback) => {
                                            categoricalGroup2GoodsUnionDataProvider.viewParam(categoricalGroup2GoodsUnionGridFilter, (entities) => {
                                                callback(entities)
                                            })
                                        }}
                                        onSelect={(row) => {
                                            setCategoricalGroup2GoodsUnionSelectedItem(row)
                                        }}
                                        onSort={(i) => dispatchCategoricalGroup2GoodsUnionGridFilter({ type: "sort", payload: i.propertyName })}
                                        onFilterDelete={(i) => dispatchCategoricalGroup2GoodsUnionGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                        onPageNumberChange={(n) => dispatchCategoricalGroup2GoodsUnionGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                        onNumberPerPageChange={(n) => dispatchCategoricalGroup2GoodsUnionGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                    />
                                </div>
                            </div>
                    }
                </div>
            </div>
            <div className={defaultStyles.rowGrids} style={{marginBottom: 15}}>
                {/* 3 Грид */}
                <div className={defaultStyles.blockWrapper}>
                    <InputSearch
                        searchValue={''}
                        typeFilter={'columnFilter'}
                        operator="Like"
                        gridFilter={categoricalGroupLinkUnionGridFilter}
                        dispatchGridFilter={dispatchCategoricalGroupLinkUnionGridFilter}
                        resetTrigger={resetTrigger}
                        filterName="categoricalGroupName"
                        placeholder={baseT("searchByName")}
                        debounceDelay={1000}
                    />
                    {
                        isCategoricalGroupLinkUnionDataLoading
                            ? <div><Spinner /></div>
                            : <div className={styles.gridWrapper}>
                                <div className={styles.masterGrid}>
                                    <DefaultGrid
                                        // hideSettingsAndExportButton
                                        showQuantity
                                        gridId={uuidv4()}
                                        data={categoricalGroupLinkUnionData}
                                        loadingStatus={loadingStatus}
                                        setLoadingStatus={setLoadingStatus}
                                        dataProvider={categoricalGroupLinkUnionDataProvider}
                                        totalCount={categoricalGroupLinkUnionTotalCount}
                                        filter={categoricalGroupLinkUnionGridFilter}
                                        plugin={CategoricalGroupLinkUnionPluginSettings()}
                                        selectedItem={categoricalGroupLinkUnionSelectedItem}
                                        getView={(categoricalGroupLinkUnionGridFilter, callback) => {
                                            categoricalGroupLinkUnionDataProvider.viewParam(categoricalGroupLinkUnionGridFilter, (entities) => {
                                                callback(entities)
                                            })
                                        }}
                                        onSelect={(row) => {
                                            setCategoricalGroupLinkUnionSelectedItem(row)
                                        }}
                                        onSort={(i) => dispatchCategoricalGroupLinkUnionGridFilter({ type: "sort", payload: i.propertyName })}
                                        onFilterDelete={(i) => dispatchCategoricalGroupLinkUnionGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                        onPageNumberChange={(n) => dispatchCategoricalGroupLinkUnionGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                        onNumberPerPageChange={(n) => dispatchCategoricalGroupLinkUnionGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                    />
                                </div>
                            </div>
                    }
                </div>
                {/* 4 Грид */}
                <div className={defaultStyles.blockWrapper}>
                    <div className={defaultStyles.searchBlock}>
                         <div className={defaultStyles.codeSearchBlock}>
                            <InputSearch
                                searchValue={''}
                                typeFilter={'columnFilter'}
                                gridFilter={relatedCategoricalGroup2GoodsUnionGridFilter}
                                dispatchGridFilter={dispatchRelatedCategoricalGroup2GoodsUnionGridFilter}
                                resetTrigger={resetTrigger}
                                filterName="goodsCode"
                                placeholder={baseT("searchByCode")}
                                debounceDelay={1000}
                            />
                         </div>
                         <div className={defaultStyles.goodsSearchBlock}>
                            <InputSearch
                                searchValue={''}
                                typeFilter={'columnFilter'}
                                operator="Like"
                                gridFilter={relatedCategoricalGroup2GoodsUnionGridFilter}
                                dispatchGridFilter={dispatchRelatedCategoricalGroup2GoodsUnionGridFilter}
                                resetTrigger={resetTrigger}
                                filterName="goodsName"
                                placeholder={baseT("searchByName")}
                                debounceDelay={1000}
                            />
                         </div>
                    </div>
                    {
                        isRelatedCategoricalGroup2GoodsUnionDataLoading
                            ? <div><Spinner /></div>
                            : <div className={styles.gridWrapper}>
                                <div className={styles.masterGrid}>
                                    <DefaultGrid
                                        showQuantity
                                        // hideSettingsAndExportButton
                                        gridId={uuidv4()}
                                        data={relatedCategoricalGroup2GoodsUnionData}
                                        loadingStatus={loadingStatus}
                                        setLoadingStatus={setLoadingStatus}
                                        dataProvider={categoricalGroup2GoodsUnionDataProvider}
                                        totalCount={relatedCategoricalGroup2GoodsUnionTotalCount}
                                        filter={relatedCategoricalGroup2GoodsUnionGridFilter}
                                        plugin={RelatedCategoricalGroup2GoodsUnionPluginSettings()}
                                        selectedItem={relatedCategoricalGroup2GoodsUnionSelectedItem}
                                        getView={(relatedCategoricalGroup2GoodsUnionGridFilter, callback) => {
                                            categoricalGroup2GoodsUnionDataProvider.viewParam(relatedCategoricalGroup2GoodsUnionGridFilter, (entities) => {
                                                callback(entities)
                                                setRelatedCategoricalGroup2GoodsUnionSelectedItem(undefined);
                                            })
                                        }}
                                        onSelect={(row) => {
                                            setRelatedCategoricalGroup2GoodsUnionSelectedItem(row)
                                        }}
                                        onSort={(i) => dispatchRelatedCategoricalGroup2GoodsUnionGridFilter({ type: "sort", payload: i.propertyName })}
                                        onFilterDelete={(i) => dispatchRelatedCategoricalGroup2GoodsUnionGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                                        onPageNumberChange={(n) => dispatchRelatedCategoricalGroup2GoodsUnionGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                        onNumberPerPageChange={(n) => dispatchRelatedCategoricalGroup2GoodsUnionGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                                    />
                                </div>
                            </div>
                    }
                </div>
            </div>
        </PluginWrapper>
    );
};